import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAppConfig } from '../../contexts/AppConfigContext';
import './ManageChildren.css';

const ManageChildren = () => {
  const { appName } = useAppConfig();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    relationshipCode: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/children');
      
      if (response.data.success) {
        setChildren(response.data.data.children);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
      setError('Failed to load children data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setFormError('Please enter the child\'s email address');
      return;
    }

    try {
      setFormLoading(true);
      setFormError('');
      
      const response = await api.post('/users/link-child', {
        email: formData.email.trim(),
        relationshipCode: formData.relationshipCode.trim() || undefined
      });

      if (response.data.success) {
        setSuccessMessage('Child linked successfully!');
        setFormData({ email: '', relationshipCode: '' });
        setShowAddForm(false);
        fetchChildren(); // Refresh the list
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error linking child:', err);
      setFormError(
        err.response?.data?.message || 'Failed to link child. Please try again.'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleUnlinkChild = async (childId, childName) => {
    if (!window.confirm(`Are you sure you want to unlink ${childName}? This will remove them from your parent dashboard.`)) {
      return;
    }

    try {
      const response = await api.post(`/users/unlink-child/${childId}`);
      
      if (response.data.success) {
        setSuccessMessage('Child unlinked successfully!');
        fetchChildren(); // Refresh the list
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error unlinking child:', err);
      setError(
        err.response?.data?.message || 'Failed to unlink child. Please try again.'
      );
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return '#4CAF50'; // Green
    if (score >= 60) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const formatStudyTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="manage-children">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading children...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-children">
      <div className="page-header">
        <h1>Manage Children</h1>
        <p>Add and manage your children's accounts</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button 
            className="close-btn"
            onClick={() => setError('')}
          >
            ×
          </button>
        </div>
      )}

      {/* Add Child Section */}
      <div className="add-child-section">
        <div className="section-header">
          <h2>Add New Child</h2>
          <button 
            className={`toggle-form-btn ${showAddForm ? 'active' : ''}`}
            onClick={() => {
              setShowAddForm(!showAddForm);
              setFormError('');
              setFormData({ email: '', relationshipCode: '' });
            }}
          >
            {showAddForm ? 'Cancel' : 'Add Child'}
          </button>
        </div>

        {showAddForm && (
          <div className="add-form-container">
            <form onSubmit={handleAddChild} className="add-child-form">
              <div className="form-group">
                <label htmlFor="email">Child's Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your child's registered email"
                  required
                  disabled={formLoading}
                />
                <small className="form-help">
                  This should be the email address your child used to register their account
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="relationshipCode">Relationship Code (Optional)</label>
                <input
                  type="text"
                  id="relationshipCode"
                  name="relationshipCode"
                  value={formData.relationshipCode}
                  onChange={handleInputChange}
                  placeholder="Enter relationship code if provided"
                  disabled={formLoading}
                />
                <small className="form-help">
                  Some accounts may require a relationship code for verification
                </small>
              </div>

              {formError && (
                <div className="form-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  {formError}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormError('');
                    setFormData({ email: '', relationshipCode: '' });
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={formLoading || !formData.email.trim()}
                >
                  {formLoading ? (
                    <>
                      <div className="btn-spinner"></div>
                      Linking...
                    </>
                  ) : (
                    'Link Child'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Children List */}
      <div className="children-list-section">
        <h2>Your Children ({children.length})</h2>
        
        {children.length === 0 ? (
          <div className="no-children">
            <div className="no-children-icon">
              <i className="fas fa-users"></i>
            </div>
            <h3>No Children Added</h3>
            <p>Start by adding your first child to monitor their progress</p>
            <button 
              className="add-first-child-btn"
              onClick={() => setShowAddForm(true)}
            >
              Add Your First Child
            </button>
          </div>
        ) : (
          <div className="children-grid">
            {children.map((child) => (
              <div key={child._id} className="child-card">
                <div className="child-header">
                  <div className="child-avatar">
                    <img 
                      src={child.profilePicture || '/default-avatar.png'} 
                      alt={child.fullName}
                      onError={(e) => {
                        e.target.src = '/default-avatar.png';
                      }}
                    />
                  </div>
                  <div className="child-info">
                    <h3>{child.fullName}</h3>
                    <p className="child-email">{child.email}</p>
                    <p className="child-grade">Grade {child.grade}</p>
                    <span className={`status ${child.isActive ? 'active' : 'inactive'}`}>
                      {child.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="child-stats">
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Performance</span>
                      <span 
                        className="stat-value"
                        style={{ color: getPerformanceColor(child.recentPerformance || 0) }}
                      >
                        {child.recentPerformance || 0}%
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Tests</span>
                      <span className="stat-value">{child.testsTaken || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Study Time</span>
                      <span className="stat-value">{formatStudyTime(child.studyTime || 0)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Last Active</span>
                      <span className="stat-value">
                        {child.lastActive ? new Date(child.lastActive).toLocaleDateString() : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="child-actions">
                  <button 
                    className="view-progress-btn"
                    onClick={() => window.location.href = `/parent/child/${child._id}/progress`}
                  >
                    <i className="fas fa-chart-line"></i>
                    View Progress
                  </button>
                  <button 
                    className="unlink-btn"
                    onClick={() => handleUnlinkChild(child._id, child.fullName)}
                  >
                    <i className="fas fa-unlink"></i>
                    Unlink
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h3>How to Add Children</h3>
        <div className="instructions-grid">
          <div className="instruction-item">
            <div className="instruction-number">1</div>
            <div className="instruction-content">
              <h4>Get Child's Email</h4>
              <p>Ask your child for the email address they used to register their {appName} account.</p>
            </div>
          </div>
          <div className="instruction-item">
            <div className="instruction-number">2</div>
            <div className="instruction-content">
              <h4>Enter Email Address</h4>
              <p>Click "Add Child" and enter their email address in the form above.</p>
            </div>
          </div>
          <div className="instruction-item">
            <div className="instruction-number">3</div>
            <div className="instruction-content">
              <h4>Link Account</h4>
              <p>Submit the form to link their account to your parent dashboard.</p>
            </div>
          </div>
          <div className="instruction-item">
            <div className="instruction-number">4</div>
            <div className="instruction-content">
              <h4>Monitor Progress</h4>
              <p>Once linked, you can view their progress, performance, and study analytics.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageChildren;